import React, { ReactElement, useRef, useState } from 'react'
import Input from 'antd/lib/input'
import Form from 'antd/lib/form'
import notification from 'antd/lib/notification'
import styles from './SupportPage.module.scss'
import { DisplayError } from '@/components/DisplayError/DisplayError'
import { reusableCssClass } from '@/utils/reusableCssClasses'
import { sendSupportEmail } from '@/api/support'
import {
    SupportForm,
    SupportSubject,
} from '@/components/support/SupportPage/SupportPage.types'
import { NotificationKeys } from '@/utils/notificationKeys'
import { UserInfoState } from '@/atoms/accountMaintenance/userInfo'
import {
    GenericButton,
    GenericButtonType,
} from '@/components/static/ui/Button/GenericButton/GenericButton'
import Select from 'antd/lib/select'

export const SupportPage: React.FC<
    | {
          isLoggedIn: false
          userInfoState: null
      }
    | {
          isLoggedIn: true
          userInfoState: UserInfoState
      }
> = (props): ReactElement => {
    const formRef = useRef(null)
    const [errorMsg, setErrorMsg] = useState<string>('')
    const [isOtherSelected, setIsOtherSelected] = useState<boolean>(false)
    const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(true)
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const handleSelectChange = async (value: SupportSubject): Promise<void> => {
        if (value === SupportSubject.OTHER) {
            setIsOtherSelected(true)
        } else {
            setIsOtherSelected(false)
        }
        handleChange()
    }

    const handleChange = (): void => {
        setErrorMsg('')
        setIsSubmitDisabled(getIsSubmitDisabled())
    }

    const handleSubmit = async (values: SupportForm): Promise<void> => {
        setIsLoading(true)
        let formEmail: string
        let name: string
        if (props.isLoggedIn) {
            formEmail = props.userInfoState?.email
            name = props.userInfoState?.displayName
        } else {
            formEmail = values.email
            name = values.name
        }
        const subject = values.subject
        let otherSubject: string
        if (isOtherSelected) {
            otherSubject = values.otherSubject
        } else {
            otherSubject = null
        }

        const body = values.body

        const response = await sendSupportEmail(
            formEmail,
            name,
            subject,
            body,
            otherSubject
        )
        if (!response.data.success) {
            setErrorMsg(response.data.error)
        } else {
            notification.success({
                message: 'Message successfully sent',
                description:
                    'Thank you for your message. We sent you an email as confirmation of receipt and will be reaching out to you soon!',
                duration: 5,
                onClick: () => {
                    notification.destroy(
                        NotificationKeys.SUPPORT_MESSAGE_SUCCESSFULLY_SENT
                    )
                },
                className: reusableCssClass.clickMe,
                key: NotificationKeys.SUPPORT_MESSAGE_SUCCESSFULLY_SENT,
            })
            formRef.current.resetFields()
        }
        setIsLoading(false)
    }

    const getIsSubmitDisabled = (): boolean => {
        if (!props.isLoggedIn) {
            if (!formRef.current.getFieldValue('email')) {
                return true
            }
            if (!formRef.current.getFieldValue('name')) {
                return true
            }
        }

        if (!formRef.current.getFieldValue('subject')) {
            return true
        }

        if (isOtherSelected && !formRef.current.getFieldValue('otherSubject')) {
            return true
        }

        return !formRef.current.getFieldValue('body')
    }

    return (
        <div className={styles.supportPageContainerContainer}>
            <div className={styles.supportPageContainer}>
                <div className={styles.headerText}>
                    <span>Contact our team</span>
                </div>
                <Form onFinish={handleSubmit} layout={'vertical'} ref={formRef}>
                    {!props.isLoggedIn && (
                        <>
                            <Form.Item
                                label={'Email Address:'}
                                name={'email'}
                                required
                                rules={[
                                    {
                                        required: true,
                                        message:
                                            'Email is required if you are not signed in',
                                    },
                                    {
                                        type: 'email',
                                        message:
                                            'Please enter a valid email address',
                                    },
                                ]}
                            >
                                <Input
                                    placeholder="john@email.com"
                                    onChange={handleChange}
                                />
                            </Form.Item>
                            <Form.Item
                                label={'Name:'}
                                name={'name'}
                                required
                                rules={[
                                    {
                                        required: true,
                                        message:
                                            'Name is required if you are not signed in',
                                    },
                                ]}
                            >
                                <Input
                                    placeholder="John"
                                    onChange={handleChange}
                                />
                            </Form.Item>
                        </>
                    )}
                    <Form.Item label={'Subject:'} name={'subject'} required>
                        <Select
                            onChange={handleSelectChange}
                            popupClassName={styles.supportSelectDropdown}
                        >
                            <Select.Option value={SupportSubject.ACCOUNT}>
                                {SupportSubject.ACCOUNT}
                            </Select.Option>
                            <Select.Option
                                value={SupportSubject.WEBSITE_FUNCTIONALITY}
                            >
                                {SupportSubject.WEBSITE_FUNCTIONALITY}
                            </Select.Option>
                            <Select.Option
                                value={SupportSubject.REACTIVATE_SUBSCRIPTION}
                            >
                                {SupportSubject.REACTIVATE_SUBSCRIPTION}
                            </Select.Option>
                            <Select.Option value={SupportSubject.REFUNDS}>
                                {SupportSubject.REFUNDS}
                            </Select.Option>
                            <Select.Option value={SupportSubject.OTHER}>
                                {SupportSubject.OTHER}
                            </Select.Option>
                        </Select>
                    </Form.Item>
                    {isOtherSelected && (
                        <Form.Item
                            name={'otherSubject'}
                            required
                            rules={[
                                {
                                    required: true,
                                    message:
                                        'Please fill out this section so we can better assist you',
                                },
                            ]}
                        >
                            <Input
                                placeholder={'Subject description'}
                                onChange={handleChange}
                            />
                        </Form.Item>
                    )}
                    <Form.Item
                        label={'How can we help?'}
                        name={'body'}
                        required
                        rules={[
                            {
                                required: true,
                                message:
                                    'Please fill out this section so we can better assist you',
                            },
                        ]}
                    >
                        <Input.TextArea
                            placeholder={
                                'Please describe the issue you are experiencing in as much detail as possible'
                            }
                            autoSize={{ minRows: 2, maxRows: 10 }}
                            allowClear
                            onChange={handleChange} // needed for the allowClear button to work properly
                        />
                    </Form.Item>
                    <div className={styles.btnContainer}>
                        <GenericButton
                            type={GenericButtonType.greenNoBackground}
                            text={'Submit'}
                            htmlType={
                                'submit'
                            } /* will automatically call handleSubmit on click or press enter */
                            onClick={null}
                            loading={isLoading}
                            disabled={isSubmitDisabled}
                        />

                        <DisplayError message={errorMsg} />
                    </div>
                </Form>
            </div>
        </div>
    )
}
